import { useId, useMemo, useState } from "react";
import { isSealedCategory } from "../../shared/market";
import { parsePriceArs } from "../../shared/marketImport";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import type { MyListing } from "./api";

/** Lowercase without accents, so "pokemon" finds "Pokémon". */
export const norm = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** Every word typed is somewhere in the name, set or number. */
export function matchesText(l: Pick<MyListing, "name" | "setName" | "cardNumber">, text: string): boolean {
  const words = norm(text).split(/\s+/).filter(Boolean);
  const hay = norm(`${l.name} ${l.setName ?? ""} ${l.cardNumber ?? ""}`);
  return words.every((w) => hay.includes(w));
}

export const LISTING_SHOW = ["all", "live", "soldout", "paused", "reserved"] as const;
export type ListingShow = (typeof LISTING_SHOW)[number];
export const LISTING_KINDS = ["all", "singles", "graded", "sealed"] as const;
export type ListingKind = (typeof LISTING_KINDS)[number];
export const LISTING_SORTS = ["recent", "price_asc", "price_desc", "name"] as const;
export type ListingSort = (typeof LISTING_SORTS)[number];

export interface ListingFilter {
  text: string;
  set: string;
  show: ListingShow;
  kind: ListingKind;
  min: string;
  max: string;
  sort: ListingSort;
}
export const NO_FILTER: ListingFilter = { text: "", set: "", show: "all", kind: "all", min: "", max: "", sort: "recent" };
const isFiltering = (f: ListingFilter) => f.text.trim() !== "" || f.set !== "" || f.show !== "all" || f.kind !== "all" || f.min.trim() !== "" || f.max.trim() !== "";

/**
 * Mi tienda › Publicaciones: the seller's own listings narrowed down (name, set, price, what's going on
 * with them) and sorted. `reserved` is the units each listing has set aside for a client.
 */
export function filterListings(listings: MyListing[], f: ListingFilter, ctx: { reserved: Map<number, number> }): MyListing[] {
  const min = f.min.trim() ? parsePriceArs(f.min) : null;
  const max = f.max.trim() ? parsePriceArs(f.max) : null;
  const out = listings.filter((l) => {
    if (f.set && (l.setName ?? "") !== f.set) return false;
    if (min !== null && l.priceArs < min) return false;
    if (max !== null && l.priceArs > max) return false;
    if (f.kind === "sealed" && !isSealedCategory(l.category)) return false;
    if (f.kind === "singles" && (isSealedCategory(l.category) || l.grader)) return false;
    if (f.kind === "graded" && !l.grader) return false;
    if (f.show === "live" && !(l.status === "active" && l.quantity > 0)) return false;
    // Out of stock because of a reservation counts as reserved, like its row says.
    if (f.show === "soldout" && !(l.status === "active" && l.quantity === 0 && !ctx.reserved.get(l.id))) return false;
    if (f.show === "paused" && l.status !== "paused") return false;
    if (f.show === "reserved" && !ctx.reserved.get(l.id)) return false;
    return matchesText(l, f.text);
  });
  if (f.sort === "price_asc") out.sort((a, b) => a.priceArs - b.priceArs);
  else if (f.sort === "price_desc") out.sort((a, b) => b.priceArs - a.priceArs);
  else if (f.sort === "name") out.sort((a, b) => a.name.localeCompare(b.name, "es"));
  return out;
}

/** The filter bar over the listings: search (with the shop's own names to pick from), set, kind, state, price range and order. */
export function ListingFilters({ listings, value, onChange, shown, reservations = false }: { listings: MyListing[]; value: ListingFilter; onChange: (f: ListingFilter) => void; shown: number; /** The plan has "Reservar para un cliente". */ reservations?: boolean }) {
  const { t } = useI18n();
  const id = useId();
  const [open, setOpen] = useState(() => isFiltering(value));
  const names = useMemo(() => [...new Set(listings.map((l) => l.name))].sort((a, b) => a.localeCompare(b, "es")), [listings]);
  const sets = useMemo(() => [...new Set(listings.map((l) => l.setName).filter((s): s is string => Boolean(s)))].sort((a, b) => a.localeCompare(b, "es")), [listings]);
  const set = <K extends keyof ListingFilter>(k: K, v: ListingFilter[K]) => onChange({ ...value, [k]: v });
  const filtering = isFiltering(value);
  return (
    <section className="listing-filters stack-sm" aria-label={t("lf.title")}>
      <div className="field-row">
        <div className="field">
          <label htmlFor={`${id}-text`}>{t("lf.search")}</label>
          <input id={`${id}-text`} type="search" list={`${id}-names`} value={value.text} onChange={(e) => set("text", e.target.value)} placeholder={t("bulk.searchHint")} />
          <datalist id={`${id}-names`}>
            {names.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        </div>
        <div className="field">
          <label htmlFor={`${id}-sort`}>{t("lf.sort")}</label>
          <select id={`${id}-sort`} value={value.sort} onChange={(e) => set("sort", e.target.value as ListingSort)}>
            {LISTING_SORTS.map((s) => (
              <option key={s} value={s}>
                {t(`lf.sort.${s}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <button type="button" className="btn btn-link btn-sm" aria-expanded={open} aria-controls={`${id}-more`} onClick={() => setOpen(!open)}>
        {open ? t("lf.less") : t("lf.more")}
      </button>
      {open && (
        <div id={`${id}-more`} className="field-row">
          {sets.length > 0 && (
            <div className="field">
              <label htmlFor={`${id}-set`}>{t("lf.set")}</label>
              <select id={`${id}-set`} value={value.set} onChange={(e) => set("set", e.target.value)}>
                <option value="">{t("lf.allSets")}</option>
                {sets.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field">
            <label htmlFor={`${id}-kind`}>{t("lf.kind")}</label>
            <select id={`${id}-kind`} value={value.kind} onChange={(e) => set("kind", e.target.value as ListingKind)}>
              {LISTING_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`lf.kind.${k}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`${id}-show`}>{t("bulk.status")}</label>
            <select id={`${id}-show`} value={value.show} onChange={(e) => set("show", e.target.value as ListingShow)}>
              {LISTING_SHOW.filter((s) => s !== "reserved" || reservations).map((s) => (
                <option key={s} value={s}>
                  {t(`lf.show.${s}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`${id}-min`}>{t("lf.min")}</label>
            <input id={`${id}-min`} inputMode="numeric" value={value.min} onChange={(e) => set("min", e.target.value)} placeholder="$" />
          </div>
          <div className="field">
            <label htmlFor={`${id}-max`}>{t("lf.max")}</label>
            <input id={`${id}-max`} inputMode="numeric" value={value.max} onChange={(e) => set("max", e.target.value)} placeholder="$" />
          </div>
        </div>
      )}
      <p className="small muted">
        <span role="status">{filtering ? t("lf.count", { shown, total: listings.length }) : ""}</span>
        {filtering && (
          <button type="button" className="btn btn-link btn-sm" onClick={() => onChange({ ...NO_FILTER, sort: value.sort })}>
            {t("lf.clear")}
          </button>
        )}
      </p>
    </section>
  );
}
